import React from "react"
import SettingContentWrapper from "@/modules/shared/SettingContentWrapper"
import {
  NotifiedStatusesTypes,
  Transporters,
  TransportersSettingsType,
} from "@/types/organizations.types"
import { OrderStatusesTypes, SORTED_STATUSES } from "@/types/statuses.types"
import { MediumText, SecondaryText } from "@/UI"
import { useTranslation } from "react-i18next"
import { keys, map, pick } from "lodash"
import StatusNotificationRow from "./StatusNotificationRow"
import TransporterSelector from "./TransporterSelector"
import { useFlags } from "flagsmith/react"
import { Alert } from "antd"
import { useRecoilValue } from "recoil"
import { userAtom } from "@/atoms/userAtom"
import { CountryKeys } from "@/types/localization.types"

interface NotificationsSettingsProps {
  notifications?: NotifiedStatusesTypes
  transporters?: TransportersSettingsType
  notificationsConfig?: NotifiedStatusesTypes
  transportersConfig?: TransportersSettingsType
  isRecipients: boolean
  updateNotifications: (status: OrderStatusesTypes, value: boolean) => void
  updateTransporters: (transporter: Transporters, value: boolean) => void
  isLoading: boolean
  disabled?: boolean
}

// TODO: Handle lower section when empty

const NotificationsSettings: React.FC<NotificationsSettingsProps> = ({
  notificationsConfig,
  transportersConfig,
  notifications,
  transporters,
  isRecipients,
  isLoading,
  updateNotifications,
  updateTransporters,
  disabled,
}) => {
  const { t } = useTranslation()
  const { org } = useRecoilValue(userAtom)

  const flags = useFlags(["notification_transporters_enabled"])
  // { transporter: boolean } as string
  const enabledTransporters = JSON.parse(
    (flags.notification_transporters_enabled.value as string) || "{}"
  )

  const channelSettings = Object.assign(
    { ...transportersConfig },
    pick(transporters, keys(transporters))
  )

  const notificationsStatusSettings = Object.assign(
    { ...notificationsConfig },
    pick(notifications, keys(notifications))
  )

  const sortStatuses = (statusObject: object) => {
    const keys = Object.keys(statusObject)
    const sortedKeys = keys.sort((a, b) => {
      return SORTED_STATUSES.indexOf(a as any) - SORTED_STATUSES.indexOf(b as any)
    })

    return sortedKeys.map((status) => ({
      status,
      active: notificationsStatusSettings[status as OrderStatusesTypes],
    }))
  }

  const sortedNotificationsSettings = sortStatuses(notificationsStatusSettings)

  return (
    <div className={`w-full ${disabled ? "opacity-50" : ""}`}>
      <SettingContentWrapper>
        <div className="flex flex-col space-y-4">
          <MediumText>{t("profile.NotificationSettings.updateChannelSettings")}</MediumText>
          {map(channelSettings, (active: boolean, channel: Transporters) => {
            if (!enabledTransporters?.[channel]) {
              return <></>
            }

            return (
              <>
                {channel === Transporters.SMS && isRecipients && org.country === CountryKeys.MY && (
                  <Alert
                    message={t("profile.NotificationSettings.MYUrlIsNotAvailableWarning")}
                    type="warning"
                  />
                )}
                <TransporterSelector
                  key={channel}
                  channel={channel as Transporters}
                  active={active}
                  onChange={updateTransporters}
                  isRecipients={isRecipients}
                  isLoading={isLoading}
                  disabled={disabled}
                />
              </>
            )
          })}
        </div>
      </SettingContentWrapper>
      <SettingContentWrapper>
        <div className="mb-2">
          <MediumText>{t("profile.NotificationSettings.updateStatusSettings")}</MediumText>
          <SecondaryText>
            {t(
              isRecipients
                ? "profile.NotificationsSettings.recipientUpdateSettingsDescription"
                : "profile.NotificationsSettings.updateSettingsDescription"
            )}
          </SecondaryText>
        </div>
        <div className="flex flex-col space-y-4 mt-4">
          {map(
            sortedNotificationsSettings,
            (statusSetting: { status: OrderStatusesTypes; active: boolean }) => (
              <StatusNotificationRow
                key={statusSetting.status}
                active={statusSetting.active}
                onChange={updateNotifications}
                status={statusSetting.status}
                isLoading={isLoading}
                disabled={disabled}
              />
            )
          )}
        </div>
      </SettingContentWrapper>
    </div>
  )
}

export default NotificationsSettings
